import { type FC } from "react";

import { Tab } from "@snapchat/snap-design-system";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { CofConfigId, MetricField } from "../../../gql-generated/types";
import { useInsightsPageBlizzardEvent } from "../../hooks";
import { type InsightsMetricsMap, type StatsItem } from "../../models";
import { ChartSkeletonContainer } from "../ChartSkeletonContainer";
import { InsightsSubTabs } from "../InsightsSubTabs";
import { SubTabContent } from "../SubTabContent";
import { SubTabHeader } from "../SubTabHeader";
import { BarChartComponent } from "../charts";

import { AdditionalInsights } from "./AdditionalInsights";
import {
    FavoritesContentName,
    PlaysContentName,
    PostersContentName,
    PostsContentName,
    SharesContentName,
    TotalFavorites,
    TotalFavoritesTooltip,
    TotalPlays,
    TotalPlaysTooltip,
    TotalPosts,
    TotalPostsTooltip,
    TotalShares,
    TotalSharesTooltip,
    TotalUniquePosters,
    TotalUniquePostersTooltip,
    TotalViews,
    TotalViewsTooltip,
    ViewsContentName,
} from "./messages.generated";

interface EngagementTabProps {
    chartsMetrics: InsightsMetricsMap<StatsItem>;
    loading: boolean;
}

export const EngagementTab: FC<EngagementTabProps> = ({ chartsMetrics, loading }) => {
    useInsightsPageBlizzardEvent("MY_LENSES_INSIGHTS_ENGAGEMENT");

    const { configs } = useFeatureGates();
    const isLensPostsEnabled = configs[CofConfigId.MyLensesLensCreatorRewardsInsights].enabled;

    return (
        <>
            <InsightsSubTabs>
                <Tab
                    title={
                        <SubTabHeader
                            label={<TotalPlays />}
                            tooltip={<TotalPlaysTooltip />}
                            statValue={chartsMetrics[MetricField.LensPlays]?.totalValueFormatted ?? ""}
                            diffValue={chartsMetrics[MetricField.LensPlays]?.diffValue}
                            loading={loading}
                            isDifflayoutColumn={isLensPostsEnabled}
                        />
                    }
                >
                    <SubTabContent>
                        <ChartSkeletonContainer skeletonActive={loading}>
                            {chartsMetrics[MetricField.LensPlays] && (
                                <BarChartComponent
                                    data={chartsMetrics[MetricField.LensPlays]}
                                    contentName={<PlaysContentName />}
                                />
                            )}
                        </ChartSkeletonContainer>
                    </SubTabContent>
                </Tab>

                <Tab
                    title={
                        <SubTabHeader
                            label={<TotalViews />}
                            tooltip={<TotalViewsTooltip />}
                            statValue={chartsMetrics[MetricField.LensViews]?.totalValueFormatted ?? ""}
                            diffValue={chartsMetrics[MetricField.LensViews]?.diffValue}
                            loading={loading}
                            isDifflayoutColumn={isLensPostsEnabled}
                        />
                    }
                >
                    <SubTabContent>
                        <ChartSkeletonContainer skeletonActive={loading}>
                            {chartsMetrics[MetricField.LensViews] && (
                                <BarChartComponent
                                    data={chartsMetrics[MetricField.LensViews]}
                                    contentName={<ViewsContentName />}
                                />
                            )}
                        </ChartSkeletonContainer>
                    </SubTabContent>
                </Tab>

                <Tab
                    title={
                        <SubTabHeader
                            label={<TotalShares />}
                            tooltip={<TotalSharesTooltip />}
                            statValue={chartsMetrics[MetricField.LensShares]?.totalValueFormatted ?? ""}
                            diffValue={chartsMetrics[MetricField.LensShares]?.diffValue}
                            loading={loading}
                            isDifflayoutColumn={isLensPostsEnabled}
                        />
                    }
                >
                    <SubTabContent>
                        <ChartSkeletonContainer skeletonActive={loading}>
                            {chartsMetrics[MetricField.LensShares] && (
                                <BarChartComponent
                                    data={chartsMetrics[MetricField.LensShares]}
                                    contentName={<SharesContentName />}
                                />
                            )}
                        </ChartSkeletonContainer>
                    </SubTabContent>
                </Tab>

                <Tab
                    title={
                        <SubTabHeader
                            label={<TotalFavorites />}
                            tooltip={<TotalFavoritesTooltip />}
                            statValue={chartsMetrics[MetricField.Favorites]?.totalValueFormatted ?? ""}
                            diffValue={chartsMetrics[MetricField.Favorites]?.diffValue}
                            loading={loading}
                            isDifflayoutColumn={isLensPostsEnabled}
                        />
                    }
                >
                    <SubTabContent>
                        <ChartSkeletonContainer skeletonActive={loading}>
                            {chartsMetrics[MetricField.Favorites] && (
                                <BarChartComponent
                                    data={chartsMetrics[MetricField.Favorites]}
                                    contentName={<FavoritesContentName />}
                                />
                            )}
                        </ChartSkeletonContainer>
                    </SubTabContent>
                </Tab>

                {isLensPostsEnabled ? (
                    <Tab
                        title={
                            <SubTabHeader
                                label={<TotalPosts />}
                                tooltip={<TotalPostsTooltip />}
                                statValue={chartsMetrics[MetricField.LensPosts]?.totalValueFormatted ?? ""}
                                diffValue={chartsMetrics[MetricField.LensPosts]?.diffValue}
                                loading={loading}
                                isDifflayoutColumn={true}
                            />
                        }
                    >
                        <SubTabContent>
                            <ChartSkeletonContainer skeletonActive={loading}>
                                {chartsMetrics[MetricField.LensPosts] && (
                                    <BarChartComponent
                                        data={chartsMetrics[MetricField.LensPosts]}
                                        contentName={<PostsContentName />}
                                    />
                                )}
                            </ChartSkeletonContainer>
                        </SubTabContent>
                    </Tab>
                ) : undefined}

                {isLensPostsEnabled ? (
                    <Tab
                        title={
                            <SubTabHeader
                                label={<TotalUniquePosters />}
                                tooltip={<TotalUniquePostersTooltip />}
                                statValue={chartsMetrics[MetricField.LensPosterUniques]?.totalValueFormatted ?? ""}
                                diffValue={chartsMetrics[MetricField.LensPosterUniques]?.diffValue}
                                loading={loading}
                                isDifflayoutColumn={true}
                            />
                        }
                    >
                        <SubTabContent>
                            <ChartSkeletonContainer skeletonActive={loading}>
                                {chartsMetrics[MetricField.LensPosterUniques] && (
                                    <BarChartComponent
                                        data={chartsMetrics[MetricField.LensPosterUniques]}
                                        contentName={<PostersContentName />}
                                    />
                                )}
                            </ChartSkeletonContainer>
                        </SubTabContent>
                    </Tab>
                ) : undefined}
            </InsightsSubTabs>

            <AdditionalInsights />
        </>
    );
};
